import React, { useCallback, useEffect, useMemo } from "react";
import { useState } from "react";
import { Check } from "lucide-react";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import Step5 from "./Step5";
import Step6 from "./Step6";
import Step7 from "./Step7";
import Step8 from "./Step8";
import Step10 from "./Step10";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import Step9 from "./Step9";
import Gnavbar from "../../components/Gnavbar";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Navbar from "../MyWebSite/Components/Navbar";
import { getRequest } from "../../helpers/api.helper";
import { UserProfileId } from "../../types/user.creation";
import { useLanguage } from "../../context/LanguageContext";

type BureauParams = {
  bureauId?: string;
  whose?: "own" | "mediator";
};

const UserRegister = () => {
  const { bureauId, whose } = useParams<BureauParams>();
  const location = useLocation();
  const extractedData = location.state?.extractedData;
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const [currentStep, setCurrentStep] = useState(1);
  const [profileId, setProfileId] = useState<UserProfileId>();
  const [profileDescriptionEnabled, setProfileDescriptionEnabled] = useState<
    0 | 1
  >(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const languageContext = useLanguage();

  const translate = useCallback(
    (key: string) => {
      return typeof languageContext?.t === "function"
        ? languageContext.t(key)
        : key;
    },

    [languageContext],
  );
  const [navbarLogo, setNavbarLogo] = useState("");
  const [bureauName, setBureauName] = useState("");
  const showTopNavbar = Boolean(bureauInfo || bureauId);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const bureauIdToUse = bureauInfo?.bureauId || bureauId;
        const response = await getRequest(`/bureau/website/${bureauIdToUse}`);
        if (response.success) {
          setProfileDescriptionEnabled(
            response.data?.profile?.profileDescriptionEnabled === 1 ? 1 : 0,
          );
          setNavbarLogo(response.data?.profile?.navbarLogo || "");
          setBureauName(response.data?.profile?.bureauName || "");
        }
      } catch (error) {
        console.error("Failed to fetch bureau config", error);
      }
    };

    fetchUser();
  }, []);

  const steps = useMemo(() => {
    const baseSteps = [
      {
        id: 1,
        title: "Basic Info",
        completed: false,
      },
      {
        id: 2,
        title: "personal_information",
        completed: false,
      },
      {
        id: 3,
        title: "religionCaste",
        completed: false,
      },
      {
        id: 4,
        title: "Education Details",
        completed: false,
      },
      {
        id: 5,
        title: "Family Details",
        completed: false,
      },
      {
        id: 6,
        title: "Property Details",
        completed: false,
      },
      {
        id: 7,
        title: "Agriculture & Flat Details",
        completed: false,
      },
      {
        id: 8,
        title: "Location Details",
        completed: false,
      },
      {
        id: 9,
        title: "Partner Preferences",
        completed: false,
      },
    ];

    // Step 10 only for /profile/:bureauId
    if (profileDescriptionEnabled === 1 && bureauId) {
      baseSteps.push({
        id: 10,
        title: translate("photosDocuments"),
        completed: false,
      });
    }

    return baseSteps;
  }, [profileDescriptionEnabled, bureauId, translate]);

  useEffect(() => {
    const validIds = steps.map((s) => s.id);
    if (!validIds.includes(currentStep)) {
      setCurrentStep(validIds[validIds.length - 1]);
    }
  }, [steps, currentStep]);

  const currentStepIndex = steps.findIndex((step) => step.id === currentStep);

  const progressPercentage =
    steps.length <= 1
      ? 100
      : Math.round((currentStepIndex / (steps.length - 1)) * 100);

  const resetFlow = () => {
    setCurrentStep(1);
    setProfileId(undefined);
    setCompletedSteps([]);
  };

  const canNavigateToStep = (stepId: number) => {
    if (stepId === currentStep) return true;
    if (completedSteps.includes(stepId)) return true;
    if (stepId === currentStep + 1 && completedSteps.includes(currentStep))
      return true;
    return false;
  };

  return (
    <div className="min-h-[var(--app-height)] w-full overflow-x-hidden bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Top Navigation */}
      {showTopNavbar &&
        (bureauInfo ? (
          <Gnavbar />
        ) : (
          <Navbar navbarLogo={navbarLogo} bureauName={bureauName} />
        ))}
      <div
        className={`mx-auto max-w-7xl overflow-x-hidden px-4 pb-[calc(var(--bottom-nav-reserved-space)+1.5rem)] pt-[calc(var(--top-safe-area)+1rem)] sm:pb-8 ${
          showTopNavbar && !bureauInfo
            ? "sm:pt-[calc(var(--top-safe-area)+9rem)]"
            : ""
        }`}
      >
        {!bureauId && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-3 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            ← Back
          </button>
        )}
        
        {/* <div className="p-2 sm:mb-4">
          <span className="text-gray-600 text-xs block">
            Get 10x better response for your profile
          </span>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Create Marriage Biodata
          </h1>
          <p className="text-base md:text-lg text-gray-600">
            Enter basic details with voice message
          </p>
        </div> */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="hidden lg:block lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-24">
              <h2 className="text-lg font-bold text-gray-900 mb-6">
                {translate("Profile Creation Steps")}
              </h2>

              <div className="space-y-4">
                {steps.map((step) => (
                  <button
                    key={step.id}
                    disabled={!canNavigateToStep(step.id)}
                    // onClick={() =>
                    //   canNavigateToStep(step.id) && setCurrentStep(step.id)
                    // }
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all
                      ${
                        currentStep === step.id
                          ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white"
                          : completedSteps.includes(step.id)
                            ? "bg-gray-50 text-gray-700 hover:bg-gray-100"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                      }
                    `}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        currentStep === step.id
                          ? "bg-white text-pink-600"
                          : "bg-white text-gray-600"
                      }`}
                    >
                      {completedSteps.includes(step.id) ? (
                        <Check size={18} />
                      ) : (
                        step.id
                      )}
                    </div>
                    <span className="text-sm font-medium">{step.title}</span>
                  </button>
                ))}
              </div>

              {/* Progress */}
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">
                    {translate("progress")}
                  </span>
                  <span className="text-sm font-bold text-pink-600">
                    {progressPercentage}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-pink-500 to-purple-600 h-full rounded-full"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="lg:hidden bg-white rounded-xl shadow-sm border p-4">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-semibold">
                {translate("step")} {currentStep} of {steps.length}
              </span>
              <span className="text-sm font-bold text-pink-600">
                {progressPercentage}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-pink-500 to-purple-600 h-full rounded-full"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
          <div className="lg:col-span-9">
            <div className="bg-white rounded-2xl shadow-lg border overflow-hidden">
              <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-6 text-white">
                <div className="flex justify-between mb-1">
                  <h2 className="text-2xl font-bold">
                    {translate("step")} {currentStep}
                  </h2>
                  <span className="text-sm bg-white/20 px-3 py-1 rounded-full">
                    {translate("step")} {currentStep} {translate("of")}{" "}
                    {steps.length}
                  </span>
                </div>
                <p className="text-pink-100">
                  {steps.find((s) => s.id === currentStep)?.title}
                </p>
              </div>
              {currentStep === 1 && (
                <Step1
                  bureauId={bureauInfo?.bureauId || ""}
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  setProfileId={setProfileId}
                  otherBureauId={bureauId}
                  whose={whose}
                  extractedData={extractedData}
                  setCompletedSteps={setCompletedSteps}
                />
              )}
              {currentStep === 2 && (
                <Step2
                  bureauId={bureauInfo?.id || ""}
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  profileId={profileId}
                  setCompletedSteps={setCompletedSteps}
                />
              )}
              {currentStep === 3 && (
                <Step3
                  bureauId={bureauInfo?.id || ""}
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  profileId={profileId}
                  setCompletedSteps={setCompletedSteps}
                />
              )}
              {currentStep === 4 && (
                <Step4
                  bureauId={bureauInfo?.id || ""}
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  profileId={profileId}
                  setCompletedSteps={setCompletedSteps}
                />
              )}
              {currentStep === 5 && (
                <Step5
                  bureauId={bureauInfo?.id || ""}
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  profileId={profileId}
                  setCompletedSteps={setCompletedSteps}
                />
              )}
              {currentStep === 6 && (
                <Step6
                  bureauId={bureauInfo?.id || ""}
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  profileId={profileId}
                  setCompletedSteps={setCompletedSteps}
                />
              )}
              {currentStep === 7 && (
                <Step7
                  bureauId={bureauInfo?.id || ""}
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  profileId={profileId}
                  setCompletedSteps={setCompletedSteps}
                />
              )}
              {currentStep === 8 && (
                <Step8
                  bureauId={bureauInfo?.id || ""}
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  profileId={profileId}
                  setCompletedSteps={setCompletedSteps}
                />
              )}
              {currentStep === 9 && (
                <Step9
                  bureauId={bureauInfo?.bureauId || bureauId || ""}
                  steps={steps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  profileId={profileId}
                  profileDescriptionEnabled={profileDescriptionEnabled}
                  setCompletedSteps={setCompletedSteps}
                  onResetFlow={resetFlow}
                />
              )}
              {currentStep === 10 &&
                profileDescriptionEnabled === 1 &&
                bureauId && (
                  <Step10
                    steps={steps}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    profileId={profileId}
                    profileDescriptionEnabled={profileDescriptionEnabled}
                    setCompletedSteps={setCompletedSteps}
                    onResetFlow={resetFlow}
                  />
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRegister;
